// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/lib/constants/routes'

/**
 * V49-UAT-02 - context behouden bij terugkeer.
 * Wanneer een module wordt geopend vanuit een interne verwijzing (Documentwijzer,
 * Regiecoach, kennisomgeving, begeleidingskaart), keert de gebruiker na opslaan
 * automatisch terug naar die bronpagina in plaats van naar het dashboard.
 * Werkt via React Router location.state; geen datamodel- of architectuurwijziging.
 */
export interface ReturnState { returnTo?: string; returnLabel?: string; scrollY?: number }

/** Bouwt de navigate-opties met bron-context. Gebruik: navigate(route, navState(ROUTES.DOCUMENTS, 'Documentwijzer')). */
export function navState(returnTo: string, returnLabel: string): { state: ReturnState } {
  const scrollY = typeof window !== 'undefined' ? window.scrollY : 0
  return { state: { returnTo, returnLabel, scrollY } }
}

/** Leest de bron-context van de huidige pagina en levert het juiste terugkeerdoel + label. */
export function useReturnNav() {
  const loc = useLocation()
  const navigate = useNavigate()
  const state = (loc.state ?? {}) as ReturnState
  const fromSource = !!state.returnTo
  const target = state.returnTo ?? ROUTES.DASHBOARD
  const label = fromSource ? `Terug naar ${state.returnLabel ?? 'overzicht'}` : 'Klaar - terug naar dashboard'

  const goBack = useCallback(async (save?: () => Promise<void>) => {
    if (save) await save()
    navigate(target, fromSource ? { state: { restoreScrollY: state.scrollY } } : undefined)
  }, [navigate, target, fromSource, state.scrollY])

  return { target, label, fromSource, goBack }
}
